import { useCallback, useMemo, useReducer } from "react";
import { toast } from "react-toastify";
import { UpdateTeamBuyback } from "../../redux/auth-reducer/auth-service";
import { readApiMessage } from "../../utils/helper/helper";

/** Shown when a save gets no answer, or an answer with nothing readable in it (MG-003.1). */
export const BUYBACK_SAVE_FAILED =
  "Changes not saved. Check your connection and select Save changes again. Your selections are kept.";

/**
 * Shown for a 401 that is not an expired token: the session's permission codes predate the save
 * endpoint (they are fixed at sign-in), or the account lost the permission since.
 */
export const BUYBACK_SAVE_UNAUTHORIZED =
  "Changes not saved. Sign out, sign in again and select Save changes. Your selections are kept.";

const EMPTY = { saved: {}, changes: {}, saving: false, error: null };

/*
 * One entry per auction, keyed by auctionId:
 *   saved   - teamId -> the mark last read from or saved to the server
 *   changes - teamId -> the mark the user picked, ONLY where it differs from `saved`; its size is
 *             the unsaved count, so clicking a box back to its saved value removes it
 *   saving  - a save request is in flight
 *   error   - the message of the last failed save, cleared by any further change
 */
function reducer(state, action) {
  const entry = state[action.auctionId] ?? EMPTY;

  switch (action.type) {
    case "load": {
      // A fresh read from the server (the row was opened, or remounted after a search or a page
      // change). Kept changes survive it, minus any the server now already holds.
      const changes = {};
      Object.entries(entry.changes).forEach(([teamId, value]) => {
        if (teamId in action.saved && action.saved[teamId] !== value) changes[teamId] = value;
      });
      return { ...state, [action.auctionId]: { ...entry, saved: action.saved, changes } };
    }
    case "toggle": {
      const current = action.teamId in entry.changes ? entry.changes[action.teamId] : !!entry.saved[action.teamId];
      const changes = { ...entry.changes };
      if (!current === !!entry.saved[action.teamId]) delete changes[action.teamId];
      else changes[action.teamId] = !current;
      return { ...state, [action.auctionId]: { ...entry, changes, error: null } };
    }
    case "discard":
      return { ...state, [action.auctionId]: { ...entry, changes: {}, error: null } };
    case "discardAll": {
      const next = {};
      Object.entries(state).forEach(([auctionId, e]) => {
        next[auctionId] = { ...e, changes: {}, error: null };
      });
      return next;
    }
    case "saving":
      return { ...state, [action.auctionId]: { ...entry, saving: true, error: null } };
    case "saved":
      return {
        ...state,
        [action.auctionId]: { ...entry, saved: { ...entry.saved, ...action.sent }, changes: {}, saving: false, error: null },
      };
    case "failed":
      return { ...state, [action.auctionId]: { ...entry, saving: false, error: action.message } };
    default:
      return state;
  }
}

/** The message a failed save shows above its table. */
function failureMessage(res) {
  // A refusal the server explained: a 200 with status false, or the 403 for a caller outside the
  // auction's manager team.
  if (res?.status === 200 || res?.status === 403) return readApiMessage(res, BUYBACK_SAVE_FAILED);
  // apiClient has already renewed an expired token, so a 401 that reaches here is a permission one.
  if (res?.status === 401) return BUYBACK_SAVE_UNAUTHORIZED;
  // No reply, or a 5xx: the selections are kept and the user can try again.
  return BUYBACK_SAVE_FAILED;
}

/**
 * Team Buyback drafts for every auction on the Auction Results page (MG-003.1).
 *
 * Kept by the page rather than by each expanded row because the row's component unmounts whenever a
 * search hides the row or the pager moves past it, and unsaved choices must survive both. Nothing is
 * sent until save(); discard() sends nothing at all.
 */
export function useBuybackDrafts() {
  const [entries, dispatch] = useReducer(reducer, {});

  const load = useCallback((auctionId, rows) => {
    const saved = {};
    rows.forEach((row) => {
      saved[row.teamId] = !!row.isBuyback;
    });
    dispatch({ type: "load", auctionId, saved });
  }, []);

  const toggle = useCallback((auctionId, teamId) => dispatch({ type: "toggle", auctionId, teamId }), []);
  const discard = useCallback((auctionId) => dispatch({ type: "discard", auctionId }), []);
  const discardAll = useCallback(() => dispatch({ type: "discardAll" }), []);

  // Saves one auction's changes in one request, and answers null when they were saved or the
  // message its panel now shows when they were not.
  const saveOne = useCallback(
    async (auctionId) => {
      const changes = entries[auctionId]?.changes ?? {};
      const teams = Object.entries(changes).map(([teamId, isBuyback]) => ({ teamId: Number(teamId), isBuyback }));
      if (!teams.length) return null;

      dispatch({ type: "saving", auctionId });
      const res = await UpdateTeamBuyback(auctionId, teams);
      if (res?.data?.status === true) {
        dispatch({ type: "saved", auctionId, sent: changes });
        return null;
      }
      const message = failureMessage(res);
      dispatch({ type: "failed", auctionId, message });
      return message;
    },
    [entries]
  );

  /**
   * Saves one auction's changes in one request.
   * @returns {Promise<boolean>} Whether they were saved.
   */
  const save = useCallback(async (auctionId) => (await saveOne(auctionId)) === null, [saveOne]);

  /**
   * Saves every auction that has changes, one request each, and shows one "Changes saved" toast
   * when all of them were.
   * @returns {Promise<string[]>} The message of each auction that was not saved; empty when all were.
   */
  const saveAll = useCallback(async () => {
    const dirty = Object.keys(entries).filter((auctionId) => Object.keys(entries[auctionId].changes).length);
    const failures = [];
    for (const auctionId of dirty) {
      // Sequential on purpose: each failure is reported against its own auction, and the rest still save.
      // eslint-disable-next-line no-await-in-loop
      const message = await saveOne(Number(auctionId));
      if (message) failures.push(message);
    }
    if (dirty.length && !failures.length) toast.success("Changes saved");
    return failures;
  }, [entries, saveOne]);

  const changedCount = useCallback((auctionId) => Object.keys(entries[auctionId]?.changes ?? {}).length, [entries]);

  // Teams changed across the whole page, and in how many auctions, for the leave dialog and the
  // navigation guard.
  const totals = useMemo(() => {
    let teams = 0;
    let auctions = 0;
    Object.values(entries).forEach((entry) => {
      const n = Object.keys(entry.changes).length;
      if (n) {
        teams += n;
        auctions += 1;
      }
    });
    return { teams, auctions };
  }, [entries]);

  return {
    entries,
    load,
    toggle,
    discard,
    discardAll,
    save,
    saveAll,
    changedCount,
    totals,
    isDirty: totals.teams > 0,
  };
}
